import { useEffect, useRef } from 'react';

/**
 * Cloudflare Turnstile, explicit render. Renders nothing when no site key is
 * configured (the API skips verification in that case too). Safe across SPA
 * navigations: the script loads once, widgets render per-mount.
 */

type TurnstileApi = {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string;
      callback: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
    }
  ) => string;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
    __tbncTurnstileQueue?: (() => void)[];
    __tbncTurnstileOnload?: () => void;
  }
}

const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=__tbncTurnstileOnload';

function whenReady(fn: () => void) {
  if (window.turnstile) {
    fn();
    return;
  }
  window.__tbncTurnstileQueue = window.__tbncTurnstileQueue ?? [];
  window.__tbncTurnstileQueue.push(fn);
  window.__tbncTurnstileOnload = () => {
    const queue = window.__tbncTurnstileQueue ?? [];
    window.__tbncTurnstileQueue = [];
    for (const cb of queue) cb();
  };
  if (!document.getElementById(SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }
}

export function Turnstile({
  siteKey,
  onToken,
}: {
  siteKey: string;
  onToken: (token: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);

  useEffect(() => {
    if (!siteKey || !ref.current) return;
    let cancelled = false;
    whenReady(() => {
      if (cancelled || !ref.current || !window.turnstile) return;
      widgetId.current = window.turnstile.render(ref.current, {
        sitekey: siteKey,
        callback: onToken,
        'expired-callback': () => onToken(''),
        'error-callback': () => onToken(''),
      });
    });
    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetId.current);
        } catch {
          // widget already gone — fine
        }
        widgetId.current = null;
      }
    };
    // onToken intentionally captured once — parent passes a stable setter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey]);

  if (!siteKey) return null;
  return <div ref={ref} className="cf-turnstile" />;
}
